/* Animated kiwi chat FAB button with multiple creative style variants.
 * The kiwi's eyes animate on hover (look side to side).
 * An optional body animation adds movement to the whole kiwi. */

import { cn } from "@/lib/utils";

type ChatButtonVariant = "classic" | "neon" | "pill" | "glass" | "speech";
type EyeAnimation = "shifty" | "slowScan" | "paranoid";
type EyeSize = "default" | "big" | "huge" | "bigPupil" | "roundPupil" | "tallPupil";
type BodyAnimation = "none" | "bounce" | "wiggle" | "peek" | "jelly" | "float" | "tilt" | "hop" | "leanIn" | "zoomPeek" | "curious" | "loom";

interface KiwiChatButtonProps {
  variant?: ChatButtonVariant;
  eyeAnimation?: EyeAnimation;
  eyeSize?: EyeSize;
  bodyAnimation?: BodyAnimation;
  /** Animation speed multiplier. 1 = default, 0.5 = half speed, 2 = double speed. */
  speed?: number;
  /** When true, plays a single bounce animation (no eyes). Toggled externally to re-trigger. */
  nudge?: boolean;
  /** When true, plays the eye animation once (without hover). Toggled externally. */
  playEyes?: boolean;
  onClick?: () => void;
  className?: string;
  label?: string;
}

/* ── Eye keyframes ───────────────────────────────────────────────────── */

const EYE_KEYFRAMES: Record<EyeAnimation, string> = {
  shifty: `
    @keyframes kiwi-shifty {
      0%   { transform: translateX(0); }
      8%   { transform: translateX(0); }
      11%  { transform: translateX(-2px); }
      34%  { transform: translateX(-2px); }
      37%  { transform: translateX(2px); }
      60%  { transform: translateX(2px); }
      63%  { transform: translateX(0); }
      100% { transform: translateX(0); }
    }
  `,
  slowScan: `
    @keyframes kiwi-slowScan {
      0%   { transform: translateX(0); }
      5%   { transform: translateX(0); }
      16%  { transform: translateX(-2.5px); }
      34%  { transform: translateX(-2.5px); }
      48%  { transform: translateX(2.5px); }
      68%  { transform: translateX(2.5px); }
      80%  { transform: translateX(0); }
      100% { transform: translateX(0); }
    }
  `,
  paranoid: `
    @keyframes kiwi-paranoid {
      0%   { transform: translateX(0); }
      3%   { transform: translateX(0); }
      5%   { transform: translateX(-2px); }
      12%  { transform: translateX(-2px); }
      14%  { transform: translateX(2px); }
      21%  { transform: translateX(2px); }
      23%  { transform: translateX(-1.5px); }
      28%  { transform: translateX(-1.5px); }
      30%  { transform: translateX(0); }
      100% { transform: translateX(0); }
    }
  `,
};

/* tallPupil: scaleY stretch + translateX movement. */
const TALL_PUPIL_KEYFRAMES: Record<EyeAnimation, string> = {
  shifty: `
    @keyframes kiwi-tall-shifty {
      0%   { transform: translateX(0) scaleY(1); }
      6%   { transform: translateX(0) scaleY(1); }
      10%  { transform: translateX(0) scaleY(2); }
      14%  { transform: translateX(0) scaleY(2); }
      17%  { transform: translateX(-2px) scaleY(2); }
      38%  { transform: translateX(-2px) scaleY(2); }
      41%  { transform: translateX(2px) scaleY(2); }
      62%  { transform: translateX(2px) scaleY(2); }
      65%  { transform: translateX(0) scaleY(2); }
      72%  { transform: translateX(0) scaleY(2); }
      76%  { transform: translateX(0) scaleY(1); }
      100% { transform: translateX(0) scaleY(1); }
    }
  `,
  slowScan: `
    @keyframes kiwi-tall-slowScan {
      0%   { transform: translateX(0) scaleY(1); }
      4%   { transform: translateX(0) scaleY(1); }
      8%   { transform: translateX(0) scaleY(2); }
      12%  { transform: translateX(0) scaleY(2); }
      22%  { transform: translateX(-2.5px) scaleY(2); }
      38%  { transform: translateX(-2.5px) scaleY(2); }
      52%  { transform: translateX(2.5px) scaleY(2); }
      70%  { transform: translateX(2.5px) scaleY(2); }
      78%  { transform: translateX(0) scaleY(2); }
      84%  { transform: translateX(0) scaleY(2); }
      88%  { transform: translateX(0) scaleY(1); }
      100% { transform: translateX(0) scaleY(1); }
    }
  `,
  paranoid: `
    @keyframes kiwi-tall-paranoid {
      0%   { transform: translateX(0) scaleY(1); }
      3%   { transform: translateX(0) scaleY(1); }
      6%   { transform: translateX(0) scaleY(2); }
      9%   { transform: translateX(0) scaleY(2); }
      11%  { transform: translateX(-2px) scaleY(2); }
      17%  { transform: translateX(-2px) scaleY(2); }
      19%  { transform: translateX(2px) scaleY(2); }
      25%  { transform: translateX(2px) scaleY(2); }
      27%  { transform: translateX(-1.5px) scaleY(2); }
      32%  { transform: translateX(-1.5px) scaleY(2); }
      34%  { transform: translateX(0) scaleY(2); }
      40%  { transform: translateX(0) scaleY(2); }
      44%  { transform: translateX(0) scaleY(1); }
      100% { transform: translateX(0) scaleY(1); }
    }
  `,
};

/* ── Body keyframes ──────────────────────────────────────────────────── */

const BODY_KEYFRAMES: Record<Exclude<BodyAnimation, "none">, string> = {
  /* Bounce: quick hop up, settle back down */
  bounce: `
    @keyframes kiwi-body-bounce {
      0%   { transform: translateY(0); }
      15%  { transform: translateY(0); }
      20%  { transform: translateY(-4px); }
      25%  { transform: translateY(0); }
      30%  { transform: translateY(-2px); }
      35%  { transform: translateY(0); }
      100% { transform: translateY(0); }
    }
  `,

  /* Wiggle: tilt left and right like saying "no no" */
  wiggle: `
    @keyframes kiwi-body-wiggle {
      0%   { transform: rotate(0deg); }
      10%  { transform: rotate(0deg); }
      14%  { transform: rotate(-6deg); }
      22%  { transform: rotate(6deg); }
      28%  { transform: rotate(-4deg); }
      34%  { transform: rotate(4deg); }
      38%  { transform: rotate(0deg); }
      100% { transform: rotate(0deg); }
    }
  `,

  /* Peek: slide up like peeking over a ledge, then slide back */
  peek: `
    @keyframes kiwi-body-peek {
      0%   { transform: translateY(0); }
      10%  { transform: translateY(0); }
      20%  { transform: translateY(-5px); }
      50%  { transform: translateY(-5px); }
      60%  { transform: translateY(0); }
      100% { transform: translateY(0); }
    }
  `,

  /* Jelly: squash and stretch like a soft blob */
  jelly: `
    @keyframes kiwi-body-jelly {
      0%   { transform: scale(1, 1); }
      12%  { transform: scale(1, 1); }
      16%  { transform: scale(1.1, 0.9); }
      22%  { transform: scale(0.9, 1.1); }
      28%  { transform: scale(1.05, 0.95); }
      34%  { transform: scale(0.97, 1.03); }
      38%  { transform: scale(1, 1); }
      100% { transform: scale(1, 1); }
    }
  `,

  /* Float: gentle continuous up-down drift */
  float: `
    @keyframes kiwi-body-float {
      0%   { transform: translateY(0); }
      50%  { transform: translateY(-3px); }
      100% { transform: translateY(0); }
    }
  `,

  /* Tilt: lean to one side curiously, then straighten */
  tilt: `
    @keyframes kiwi-body-tilt {
      0%   { transform: rotate(0deg); }
      15%  { transform: rotate(0deg); }
      25%  { transform: rotate(8deg); }
      55%  { transform: rotate(8deg); }
      65%  { transform: rotate(0deg); }
      100% { transform: rotate(0deg); }
    }
  `,

  /* Hop: quick hop to the side and back — playful energy */
  hop: `
    @keyframes kiwi-body-hop {
      0%   { transform: translate(0, 0); }
      10%  { transform: translate(0, 0); }
      14%  { transform: translate(2px, -3px); }
      18%  { transform: translate(2px, 0); }
      22%  { transform: translate(2px, 0); }
      26%  { transform: translate(-2px, -3px); }
      30%  { transform: translate(-2px, 0); }
      34%  { transform: translate(-2px, 0); }
      38%  { transform: translate(0, -2px); }
      42%  { transform: translate(0, 0); }
      100% { transform: translate(0, 0); }
    }
  `,

  /* Lean In: slowly scales up + slight tilt, like peering at you with curiosity */
  leanIn: `
    @keyframes kiwi-body-leanIn {
      0%   { transform: scale(1) rotate(0deg); }
      8%   { transform: scale(1) rotate(0deg); }
      25%  { transform: scale(1.2) rotate(-4deg); }
      55%  { transform: scale(1.2) rotate(-4deg); }
      72%  { transform: scale(1) rotate(0deg); }
      100% { transform: scale(1) rotate(0deg); }
    }
  `,

  /* Zoom Peek: quick pop forward with a micro-bounce, like "oh! hello!" */
  zoomPeek: `
    @keyframes kiwi-body-zoomPeek {
      0%   { transform: scale(1) translateY(0); }
      6%   { transform: scale(1) translateY(0); }
      10%  { transform: scale(1.25) translateY(-2px); }
      14%  { transform: scale(1.18) translateY(0); }
      18%  { transform: scale(1.22) translateY(-1px); }
      50%  { transform: scale(1.22) translateY(-1px); }
      60%  { transform: scale(1) translateY(0); }
      100% { transform: scale(1) translateY(0); }
    }
  `,

  /* Curious: grows bigger while tilting side to side, examining the user */
  curious: `
    @keyframes kiwi-body-curious {
      0%   { transform: scale(1) rotate(0deg); }
      8%   { transform: scale(1) rotate(0deg); }
      16%  { transform: scale(1.15) rotate(5deg); }
      30%  { transform: scale(1.15) rotate(5deg); }
      40%  { transform: scale(1.2) rotate(-5deg); }
      55%  { transform: scale(1.2) rotate(-5deg); }
      65%  { transform: scale(1.15) rotate(3deg); }
      75%  { transform: scale(1.15) rotate(3deg); }
      85%  { transform: scale(1) rotate(0deg); }
      100% { transform: scale(1) rotate(0deg); }
    }
  `,

  /* Loom: snap big, hold ominously, snap back */
  loom: `
    @keyframes kiwi-body-loom {
      0%   { transform: scale(1) translateY(0); }
      5%   { transform: scale(1) translateY(0); }
      8%   { transform: scale(1.3) translateY(-3px); }
      62%  { transform: scale(1.3) translateY(-3px); }
      65%  { transform: scale(1) translateY(0); }
      100% { transform: scale(1) translateY(0); }
    }
  `,
};

const BODY_BASE_DURATIONS: Record<Exclude<BodyAnimation, "none">, number> = {
  bounce: 2.0,
  wiggle: 2.2,
  peek: 2.8,
  jelly: 2.4,
  float: 2.0,
  tilt: 2.6,
  hop: 3.0,
  leanIn: 3.0,
  zoomPeek: 2.4,
  curious: 3.2,
  loom: 3.0,
};

/* ── Eye geometry per size ───────────────────────────────────────────── */

interface EyeGeometry {
  whiteX: number;
  whiteY: number;
  whiteW: number;
  whiteH: number;
  whiteRx?: number;
  pupilX: number;
  pupilY: number;
  pupilW: number;
  pupilH: number;
  pupilRx?: number;
}

const EYE_SIZES: Record<EyeSize, EyeGeometry> = {
  default:    { whiteX: 10, whiteY: 10, whiteW: 7,  whiteH: 6,  pupilX: 13, pupilY: 12, pupilW: 2, pupilH: 2 },
  big:        { whiteX: 9,  whiteY: 9,  whiteW: 9,  whiteH: 8,  pupilX: 13, pupilY: 12, pupilW: 2, pupilH: 3 },
  huge:       { whiteX: 8,  whiteY: 8,  whiteW: 11, whiteH: 10, whiteRx: 2, pupilX: 12, pupilY: 12, pupilW: 3, pupilH: 3 },
  bigPupil:   { whiteX: 9,  whiteY: 9,  whiteW: 9,  whiteH: 8,  pupilX: 12, pupilY: 11, pupilW: 4, pupilH: 4 },
  roundPupil: { whiteX: 9,  whiteY: 9,  whiteW: 9,  whiteH: 8,  whiteRx: 1, pupilX: 12, pupilY: 11, pupilW: 4, pupilH: 4, pupilRx: 2 },
  tallPupil:  { whiteX: 9,  whiteY: 9,  whiteW: 9,  whiteH: 8,  pupilX: 12, pupilY: 11, pupilW: 3, pupilH: 3 },
};

const BASE_DURATIONS: Record<EyeAnimation, number> = {
  shifty: 2.4,
  slowScan: 3.2,
  paranoid: 2.8,
};

/* ── Animated Kiwi Icon ──────────────────────────────────────────────── */

interface AnimatedKiwiIconProps {
  className?: string;
  eyeAnimation?: EyeAnimation;
  eyeSize?: EyeSize;
  bodyAnimation?: BodyAnimation;
  speed?: number;
  nudge?: boolean;
  playEyes?: boolean;
  /** When true, eyes animate infinitely without requiring hover. */
  loop?: boolean;
}

export function AnimatedKiwiIcon({
  className,
  eyeAnimation = "shifty",
  eyeSize = "default",
  bodyAnimation = "none",
  speed = 1,
  nudge = false,
  playEyes: _playEyes = false,
  loop = false,
}: AnimatedKiwiIconProps) {
  const isTall = eyeSize === "tallPupil";
  const eyeAnimName = isTall ? `kiwi-tall-${eyeAnimation}` : `kiwi-${eyeAnimation}`;
  const eyeKeyframes = isTall ? TALL_PUPIL_KEYFRAMES[eyeAnimation] : EYE_KEYFRAMES[eyeAnimation];
  const eyeDuration = `${BASE_DURATIONS[eyeAnimation] / speed}s`;
  const eye = EYE_SIZES[eyeSize];

  const pupilOrigin = isTall
    ? `${eye.pupilX + eye.pupilW / 2}px ${eye.pupilY + eye.pupilH / 2}px`
    : undefined;

  const hasBody = bodyAnimation !== "none";
  const bodyAnimName = hasBody ? `kiwi-body-${bodyAnimation}` : "";
  const bodyKeyframes = hasBody ? BODY_KEYFRAMES[bodyAnimation] : "";
  const bodyDuration = hasBody ? `${BODY_BASE_DURATIONS[bodyAnimation] / speed}s` : "";
  /* Float/loom/leanIn/curious use eased timing for organic feel, rest use linear with pauses baked in */
  const EASED_BODY_ANIMS: BodyAnimation[] = ["float", "leanIn", "curious"];
  const bodyTiming = EASED_BODY_ANIMS.includes(bodyAnimation) ? "ease-in-out" : "linear";

  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("size-8 kiwi-body", loop && "kiwi-loop", className)}
    >
      <style>{`
        ${eyeKeyframes}
        ${bodyKeyframes}
        ${nudge ? `
        @keyframes kiwi-nudge-bounce {
          0%   { transform: translateY(0) scale(1); }
          10%  { transform: translateY(0) scale(1); }
          16%  { transform: translateY(-10px) scale(1.15); }
          22%  { transform: translateY(0) scale(1.05); }
          28%  { transform: translateY(-6px) scale(1.12); }
          34%  { transform: translateY(0) scale(1.02); }
          40%  { transform: translateY(-3px) scale(1.06); }
          46%  { transform: translateY(0) scale(1); }
          100% { transform: translateY(0) scale(1); }
        }
        ` : ""}
        .kiwi-btn:hover .kiwi-pupil,
        .kiwi-btn:focus-visible .kiwi-pupil,
        .kiwi-btn.kiwi-eyes-play .kiwi-pupil {
          animation: ${eyeAnimName} ${eyeDuration} linear 1;
          animation-fill-mode: forwards;
          ${pupilOrigin ? `transform-origin: ${pupilOrigin};` : ""}
        }
        ${loop ? `
        .kiwi-loop .kiwi-pupil {
          animation: ${eyeAnimName} ${eyeDuration} linear infinite;
          ${pupilOrigin ? `transform-origin: ${pupilOrigin};` : ""}
        }
        ` : ""}
        ${hasBody ? `
        .kiwi-btn:hover .kiwi-body,
        .kiwi-btn:focus-visible .kiwi-body {
          animation: ${bodyAnimName} ${bodyDuration} ${bodyTiming} 1;
          animation-fill-mode: forwards;
        }
        ` : ""}
        ${nudge ? `
        .kiwi-btn.kiwi-nudge .kiwi-body {
          animation: kiwi-nudge-bounce ${2.4 / speed}s ease-out 1;
          animation-fill-mode: forwards;
        }
        ` : ""}
      `}</style>
      {/* Body */}
      <path
        d="M12 6H20V8H24V10H26V18H24V22H20V24H12V22H8V18H6V10H8V8H12V6Z"
        fill="#22c55e"
      />
      {/* Highlight */}
      <rect x="20" y="10" width="2" height="2" fill="#4ade80" opacity="0.5" />
      <rect x="22" y="12" width="2" height="2" fill="#4ade80" opacity="0.3" />
      {/* Beak */}
      <path d="M4 12H6V14H4V12ZM2 14H4V16H2V14ZM4 16H6V18H4V16Z" fill="#d97706" />
      {/* Tuft */}
      <path d="M10 4H12V6H10V4ZM8 2H10V4H8V2ZM12 2H14V4H12V2Z" fill="#16a34a" />
      {/* Feet */}
      <path d="M12 24H14V28H12V24ZM10 28H16V30H10V28Z" fill="#16a34a" />
      <path d="M18 24H20V28H18V24ZM16 28H22V30H16V28Z" fill="#16a34a" />
      {/* Eye white */}
      <rect
        x={eye.whiteX}
        y={eye.whiteY}
        width={eye.whiteW}
        height={eye.whiteH}
        rx={eye.whiteRx ?? 0}
        fill="white"
      />
      {/* Pupil — animated */}
      <g className="kiwi-pupil">
        <rect x={eye.pupilX} y={eye.pupilY} width={eye.pupilW} height={eye.pupilH} rx={eye.pupilRx ?? 0} fill="#1a1a1a" />
      </g>
    </svg>
  );
}

/* ── Button variants ─────────────────────────────────────────────────── */

export function KiwiChatButton({
  variant = "classic",
  eyeAnimation = "shifty",
  eyeSize = "default",
  bodyAnimation = "none",
  speed = 1,
  nudge = false,
  playEyes = false,
  onClick,
  className,
  label = "Chat with Kiwi",
}: KiwiChatButtonProps) {
  const kiwiIcon = <AnimatedKiwiIcon eyeAnimation={eyeAnimation} eyeSize={eyeSize} bodyAnimation={bodyAnimation} speed={speed} nudge={nudge} playEyes={playEyes} />;

  switch (variant) {
    case "classic":
      return (
        <button
          onClick={onClick}
          className={cn(
            "kiwi-btn group relative flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground border border-border shadow-lg transition-all duration-200 hover:scale-110 hover:shadow-xl focus-visible:outline-ring/50 cursor-pointer",
            nudge && "kiwi-nudge",
            playEyes && "kiwi-eyes-play",
            className,
          )}
          aria-label={label}
        >
          {kiwiIcon}
        </button>
      );

    case "neon":
      return (
        <button
          onClick={onClick}
          className={cn(
            "kiwi-btn group relative flex h-16 w-16 items-center justify-center rounded-full shadow-lg transition-all duration-300 hover:scale-105 focus-visible:outline-ring/50 cursor-pointer",
            className,
          )}
          style={{ background: "linear-gradient(135deg, #059669, #22c55e)" }}
          aria-label={label}
        >
          <span
            className="absolute inset-0 rounded-full opacity-50 animate-ping"
            style={{ background: "linear-gradient(135deg, #059669, #22c55e)" }}
          />
          <span className="absolute -inset-1.5 rounded-full blur-lg opacity-30 group-hover:opacity-50 transition-opacity" style={{ background: "#22c55e" }} />
          <AnimatedKiwiIcon eyeAnimation={eyeAnimation} eyeSize={eyeSize} bodyAnimation={bodyAnimation} speed={speed} className="relative size-9 drop-shadow-[0_0_6px_rgba(34,197,94,0.6)]" />
        </button>
      );

    case "pill":
      return (
        <button
          onClick={onClick}
          className={cn(
            "kiwi-btn group flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-primary-foreground shadow-lg transition-all duration-200 hover:shadow-xl hover:gap-3 focus-visible:outline-ring/50 cursor-pointer",
            className,
          )}
          aria-label={label}
        >
          <AnimatedKiwiIcon eyeAnimation={eyeAnimation} eyeSize={eyeSize} bodyAnimation={bodyAnimation} speed={speed} className="size-7" />
          <span className="text-sm font-medium whitespace-nowrap">{label}</span>
        </button>
      );

    case "glass":
      return (
        <button
          onClick={onClick}
          className={cn(
            "kiwi-btn group relative flex h-14 w-14 items-center justify-center rounded-full border border-border/60 shadow-lg transition-all duration-200 hover:scale-110 hover:shadow-xl focus-visible:outline-ring/50 cursor-pointer",
            className,
          )}
          style={{
            background: "rgba(255,255,255,0.7)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
          }}
          aria-label={label}
        >
          <span className="absolute inset-0.5 rounded-full border border-green-400/0 group-hover:border-green-400/40 transition-colors duration-300" />
          <AnimatedKiwiIcon eyeAnimation={eyeAnimation} eyeSize={eyeSize} bodyAnimation={bodyAnimation} speed={speed} className="relative size-8" />
        </button>
      );

    case "speech":
      return (
        <button
          onClick={onClick}
          className={cn(
            "kiwi-btn group relative flex h-14 w-14 items-center justify-center rounded-2xl rounded-br-none bg-primary text-primary-foreground shadow-lg transition-all duration-200 hover:scale-110 hover:shadow-xl focus-visible:outline-ring/50 cursor-pointer",
            className,
          )}
          aria-label={label}
        >
          <span className="absolute -bottom-1.5 -right-0.5 h-3 w-3 rotate-45 bg-primary" />
          <AnimatedKiwiIcon eyeAnimation={eyeAnimation} eyeSize={eyeSize} bodyAnimation={bodyAnimation} speed={speed} className="relative size-8" />
        </button>
      );
  }
}
